/**
 * @file     messageRef.ts
 * @purpose  Factory functions for creating MessageRef and LocalizedTextRef objects, plus type-guard helpers used by the content resolution pipeline.
 */
import type { LocalizedTextRef, MessageKey, MessageRef, MessageValues } from './types';

export const messageRef = <Key extends MessageKey>(
  key: Key,
  values?: MessageValues,
): MessageRef<Key> => ({
  kind: 'message_ref',
  key,
  values,
});

export const localizedText = <Key extends string>(
  key: Key,
  defaultValue: string,
  values?: MessageValues,
): LocalizedTextRef<Key> => ({
  kind: 'localized_text_ref',
  key,
  defaultValue,
  values,
});

export const isMessageRef = (value: unknown): value is MessageRef => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return (
    'kind' in value &&
    'key' in value &&
    (value as { kind?: unknown }).kind === 'message_ref'
  );
};

export const isLocalizedTextRef = (value: unknown): value is LocalizedTextRef => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return (
    'kind' in value &&
    'key' in value &&
    'defaultValue' in value &&
    (value as { kind?: unknown }).kind === 'localized_text_ref'
  );
};
